/** @format */

// src/components/AdminExportScreen.js
import { useState, useEffect } from '@wordpress/element';

const AdminExportScreen = () => {
	const [meetings, setMeetings] = useState([]);
	const [selectedMeetingPostId, setSelectedMeetingPostId] = useState('');
	const [meetingDetails, setMeetingDetails] = useState(null);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);

	// Multi-Layer Filtering States (Aligned to WA CamelCase convention)
	const [filterGender, setFilterGender] = useState('ALL');
	const [filterClass, setFilterClass] = useState('ALL');
	const [filterEvent, setFilterEvent] = useState('ALL');
	const [filterClub, setFilterClub] = useState('ALL');
	const [filterStatus, setFilterStatus] = useState('ALL');

	// Sorting and Engine Format parameters
	const [sortBy, setSortBy] = useState('clubName');
	const [sortOrder, setSortOrder] = useState('ASC');
	const [exportFormat, setExportFormat] = useState('csv'); // 'csv' or 'rtf'

	// Fetch master meeting configurations on layout init
	useEffect(() => {
		fetch(`${ammData.api_url}/admin/meetings`, {
			headers: { 'X-WP-Nonce': ammData.nonce },
		})
			.then((res) => {
				if (!res.ok) throw new Error('Network response was not ok');
				return res.json();
			})
			.then((data) => {
				if (Array.isArray(data) && data.length > 0) {
					setMeetings(data);
					// Use meetingPostId matching if provided by backend, fallback to id
					setSelectedMeetingPostId(data[0].meetingPostId || data[0].id);
				}
			})
			.catch((err) => console.error('Error loading administration meetings master-list:', err));
	}, []);

	// Fetch deep analytical criteria when target track meet selection changes
	useEffect(() => {
		if (!selectedMeetingPostId) {
			setMeetingDetails(null);
			return;
		}

		setIsLoadingDetails(true);
		fetch(`${ammData.api_url}/meetings/${selectedMeetingPostId}`, {
			headers: { 'X-WP-Nonce': ammData.nonce },
		})
			.then((res) => {
				if (!res.ok) throw new Error('Failed to resolve target meet parameters');
				return res.json();
			})
			.then((details) => {
				setMeetingDetails(details);
				// Reset granular filtration streams to protect system alignment indexes
				setFilterEvent('ALL');
				setFilterClass('ALL');
				setFilterClub('ALL');
				setFilterGender('ALL');
				setFilterStatus('ALL');
			})
			.catch((err) => console.error('Error mapping backend criteria profiles:', err))
			.finally(() => setIsLoadingDetails(false));
	}, [selectedMeetingPostId]);

	const handleExportTrigger = () => {
		if (!selectedMeetingPostId) return;

		// Construct Query Parameters safely using CamelCase matching the WA database layer
		const params = new URLSearchParams({
			meetingPostId: selectedMeetingPostId,
			sexCode: filterGender,
			ageClass: filterClass,
			disciplineId: filterEvent,
			clubName: filterClub,
			status: filterStatus,
			sortBy: sortBy,
			sortOrder: sortOrder,
			format: exportFormat,
		});

		// Trigger safe physical browser download pipeline
		window.location.href = `${ammData.api_url}/admin/export?${params.toString()}&_wpnonce=${ammData.nonce}`;
	};

	// Safe extraction cascades matching modern amm_meeting_entries / amm_meeting_schedule naming conventions
	const rawEventsArray = meetingDetails?.events || meetingDetails?.entries || [];

	const eventOptions = Array.isArray(rawEventsArray) ? [...new Set(rawEventsArray.map((e) => e?.disciplineName || e?.disciplineId).filter(Boolean))] : [];

	const classOptions = Array.isArray(rawEventsArray) ? [...new Set(rawEventsArray.map((e) => e?.ageClass).filter(Boolean))] : [];

	const clubOptions = Array.isArray(rawEventsArray) ? [...new Set(rawEventsArray.map((e) => e?.clubName).filter(Boolean))] : [];

	return (
		<div className="wrap advanced-mm-export-container" style={{ marginTop: '20px', padding: '23px', backgroundColor: '#ffffff', borderRadius: '4px', border: '1px solid #ccd0d4' }}>
			<h1 className="wp-heading-inline" style={{ fontSize: '23px', fontWeight: '600', margin: '0 0 8px 0' }}>
				Configure Entries Export Data Stream
			</h1>
			<p className="description" style={{ color: '#646970', fontSize: '13px', margin: '0 0 20px 0' }}>
				Select your destination target schedule, narrow down database entries using strict performance matrices, and generate native tracking sheets.
			</p>

			<table className="form-table" role="presentation">
				<tbody>
					<tr>
						<th scope="row" style={{ width: '200px', fontWeight: '600', padding: '20px 10px 20px 0' }}>
							<label htmlFor="amm-meet-selector">Target Track Meet Competition</label>
						</th>
						<td>
							<select id="amm-meet-selector" value={selectedMeetingPostId} onChange={(e) => setSelectedMeetingPostId(e.target.value)} className="regular-text" style={{ maxWidth: '25rem', width: '100%' }}>
								<option value="">-- Choose Active Competition Pool --</option>
								{meetings.map((m) => {
									const mId = m.meetingPostId || m.id;
									return (
										<option key={mId} value={mId}>
											{m.title || m.post_title || `Meet #${mId}`}
										</option>
									);
								})}
							</select>
						</td>
					</tr>

					<tr>
						<th scope="row" style={{ fontWeight: '600', padding: '20px 10px 20px 0' }}>
							Format Engine Style
						</th>
						<td>
							<fieldset style={{ border: 'none', padding: '0', margin: '0' }}>
								<legend className="screen-reader-text">
									<span>Export Format Engine</span>
								</legend>
								<label style={{ marginRight: '25px', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
									<input type="radio" name="exportFormat" value="csv" checked={exportFormat === 'csv'} onChange={() => setExportFormat('csv')} style={{ margin: '0 6px 0 0' }} />
									<span>
										<strong>Spreadsheet Data (CSV)</strong> — Matrix formats perfect for Excel, Google Sheets, or Hy-Tek imports.
									</span>
								</label>
								<br />
								<label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', marginTop: '8px' }}>
									<input type="radio" name="exportFormat" value="rtf" checked={exportFormat === 'rtf'} onChange={() => setExportFormat('rtf')} style={{ margin: '0 6px 0 0' }} />
									<span>
										<strong>Word Document Report (RTF)</strong> — Formatted structures ready to print for official recording desks.
									</span>
								</label>
							</fieldset>
						</td>
					</tr>

					<tr>
						<th scope="row" style={{ fontWeight: '600', padding: '20px 10px 20px 0' }}>
							Refinement Filters
						</th>
						<td>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', maxWidth: '800px' }}>
								<div>
									<label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#50575e', marginBottom: '4px' }}>Discipline Event</label>
									<select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} style={{ width: '100%' }} disabled={isLoadingDetails || !selectedMeetingPostId}>
										<option value="ALL">All Discipline Events ({eventOptions.length})</option>
										{eventOptions.map((opt) => (
											<option key={opt} value={opt}>
												{opt}
											</option>
										))}
									</select>
								</div>

								<div>
									<label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#50575e', marginBottom: '4px' }}>Age Classification</label>
									<select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} style={{ width: '100%' }} disabled={isLoadingDetails || !selectedMeetingPostId}>
										<option value="ALL">All Age Classes ({classOptions.length})</option>
										{classOptions.map((opt) => (
											<option key={opt} value={opt}>
												{opt}
											</option>
										))}
									</select>
								</div>

								<div>
									<label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#50575e', marginBottom: '4px' }}>Gender Context</label>
									<select value={filterGender} onChange={(e) => setFilterGender(e.target.value)} style={{ width: '100%' }} disabled={isLoadingDetails || !selectedMeetingPostId}>
										<option value="ALL">All Genders</option>
										<option value="M">Male (M)</option>
										<option value="F">Female (F)</option>
									</select>
								</div>

								<div>
									<label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#50575e', marginBottom: '4px' }}>Club / School Hub</label>
									<select value={filterClub} onChange={(e) => setFilterClub(e.target.value)} style={{ width: '100%' }} disabled={isLoadingDetails || !selectedMeetingPostId}>
										<option value="ALL">All Affiliations ({clubOptions.length})</option>
										{clubOptions.map((opt) => (
											<option key={opt} value={opt}>
												{opt}
											</option>
										))}
									</select>
								</div>

								<div>
									<label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#50575e', marginBottom: '4px' }}>Verification Status</label>
									<select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: '100%' }} disabled={isLoadingDetails || !selectedMeetingPostId}>
										<option value="ALL">All Status Allocations</option>
										<option value="OK">Reset / OK Only</option>
										<option value="VERIFIED">Verified Entries Only</option>
										<option value="SCRATCHED">Scratched / Removed Only</option>
									</select>
								</div>
							</div>
							{isLoadingDetails && (
								<p className="description" style={{ marginTop: '8px', color: '#2271b1' }}>
									<span className="spinner is-active" style={{ float: 'none', margin: '0 5px 0 0' }}></span> Parsing data tracks...
								</p>
							)}
						</td>
					</tr>

					<tr>
						<th scope="row" style={{ fontWeight: '600', padding: '20px 10px 20px 0' }}>
							Primary Sort Matrix Hierarchy
						</th>
						<td>
							<div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
								<select aria-label="Sort configuration field" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
									<option value="clubName">Club / School Affiliation</option>
									<option value="disciplineName">Registered Discipline Event</option>
									<option value="ageClass">Age Group Classification</option>
									<option value="lastName">Competitor Surname Alphabetical</option>
									<option value="status">Status Priority Mapping</option>
								</select>

								<select aria-label="Sort direction ordering orientation" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
									<option value="ASC">Ascending (A-Z / Minimum)</option>
									<option value="DESC">Descending (Z-A / Maximum)</option>
								</select>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<div className="submit" style={{ padding: '15px 0 0 0', margin: '20px 0 0 0', borderTop: '1px solid #f0f0f1' }}>
				<button type="button" className="button button-primary button-large" onClick={handleExportTrigger} disabled={!selectedMeetingPostId || isLoadingDetails} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '32px', lineHeight: '30px' }}>
					<span className="dashicons dashicons-download" style={{ fontSize: '17px', width: '17px', height: '17px', lineHeight: '17px' }}></span>
					Compile and Download File Matrix
				</button>
			</div>
		</div>
	);
};

export default AdminExportScreen;
